'use client'

import { useRef, useState } from 'react'
import Link from 'next/link'
import { useQueryClient } from '@tanstack/react-query'
import { AdvertisingModalEmbedded } from '@/AdvertisingModal/AdvertisingModalEmbedded'
import type { AdvertisingModalFormData } from '@/AdvertisingModal/types'
import { useStore } from '@/store'
import { useCustomerSegmentsQuery } from '@/generated/graphql'
import { useTurnstile } from '@/Turnstile'
import { fetchData } from '@/lib/fetcher'
import { useStorefrontDisplayCurrency } from '@/useStorefrontDisplayCurrency'
import { EMAIL_REGEX } from '@/validation'
import { safeLocalStorage } from '@/AdvertisingModal/utils/errorHandling'
import { DAILY_DEALS_GUEST_SUBSCRIBED_KEY } from './dailyDealsPopupPolicy'
import { tracking } from '@/tracking'

export type DailyDealsSignupProps = {
  variant?: 'panel' | 'checkout'
  className?: string
  placement?: 'page' | 'checkout' | 'modal'
}

const subscribeDocument = `mutation SubscribeToDailyDeals($name: String, $email: String, $turnstileToken: String) {
  subscribeToDailyDeals(name: $name, email: $email, turnstileToken: $turnstileToken)
}`

/** Campaign adapter: membership and API policy live here; AdvertisingModal owns the UI. */
export function DailyDealsSignup({
  variant = 'panel',
  className = '',
  placement = variant === 'checkout' ? 'checkout' : 'page',
}: DailyDealsSignupProps) {
  const { currencySymbol } = useStorefrontDisplayCurrency()
  const userId = useStore(state => state.user?.id ?? null)
  const ready = useStore(state => state.hydrated && state.authResolved)
  const [expanded, setExpanded] = useState(variant === 'panel')
  const [successViewer, setSuccessViewer] = useState<string | null>(null)
  const viewer = userId == null ? 'guest' : `customer:${userId}`
  const locked = useRef(false)
  const queryClient = useQueryClient()
  const { executeTurnstile } = useTurnstile('daily-deals')
  const segments = useCustomerSegmentsQuery(
    {},
    {
      queryKey: ['CustomerSegments', 'daily-deals', userId],
      enabled: ready && userId != null,
      staleTime: 60_000,
    }
  )
  const checking = !ready || (userId != null && segments.isPending)
  const unknown =
    userId != null &&
    !checking &&
    (!segments.isSuccess || segments.data?.customerSegments == null)
  const subscribed = segments.data?.customerSegments?.segments?.some(
    segment => segment?.id === 20
  )
  const justSubscribed = successViewer === viewer

  async function subscribe(data: AdvertisingModalFormData) {
    if (locked.current) throw new Error('Signup is already in progress.')
    locked.current = true
    tracking.track('Daily deals signup submitted', {
      placement,
      signedIn: userId != null,
    })
    try {
      let turnstileToken: string | undefined
      if (userId == null) {
        try {
          turnstileToken = await executeTurnstile()
        } catch {
          throw new Error('Verification failed. Please try again.')
        }
      }
      if ((useStore.getState().user?.id ?? null) !== userId)
        throw new Error('Your sign-in has changed. Please try again.')
      const result = await fetchData<
        { subscribeToDailyDeals: boolean },
        { name?: string; email?: string; turnstileToken?: string }
      >(
        subscribeDocument,
        userId == null
          ? {
              name: data.name?.trim(),
              email: data.email.trim(),
              turnstileToken,
            }
          : {}
      )()
      if (!result.subscribeToDailyDeals)
        throw new Error('We couldn’t subscribe you. Please try again.')
      if (userId == null)
        safeLocalStorage.setItem(DAILY_DEALS_GUEST_SUBSCRIBED_KEY, 'true')
      setSuccessViewer(viewer)
      if (userId != null)
        void queryClient.invalidateQueries({ queryKey: ['CustomerSegments'] })
    } finally {
      locked.current = false
    }
  }

  if (
    variant === 'checkout' &&
    (checking || unknown || (subscribed && !justSubscribed))
  )
    return null
  return (
    <section aria-label="Get our Deal of the Day emails" className={className}>
      {checking || unknown || (subscribed && !justSubscribed) ? (
        <div className="rounded-xl border bg-gray-50 p-6">
          <h2 className="text-xl font-semibold">
            Get our Deal of the Day emails
          </h2>
          {checking ? (
            <p role="status">Checking your email preferences…</p>
          ) : unknown ? (
            <>
              <p>We couldn’t check your email preferences.</p>
              <button
                type="button"
                className="underline"
                onClick={() => void segments.refetch()}
              >
                Try again
              </button>
            </>
          ) : (
            <DailyDealsSubscribedMessage />
          )}
        </div>
      ) : !expanded && userId == null ? (
        <button
          type="button"
          className="rounded-lg border bg-gray-50 p-5 font-semibold"
          onClick={() => setExpanded(true)}
        >
          Sign up for daily deals
        </button>
      ) : (
        <div key={viewer}>
          <AdvertisingModalEmbedded
            onSubmit={subscribe}
            withinForm={variant === 'checkout'}
            trackingEventName="Daily deals signup succeeded"
            trackingProperties={{ placement, signedIn: userId != null }}
            callbacks={{
              onModalShown: () =>
                tracking.track('Daily deals signup viewed', {
                  placement,
                  signedIn: userId != null,
                }),
              onFormError: () =>
                tracking.track('Daily deals signup failed', { placement }),
            }}
            config={{ layout: 'stacked', showFormForLoggedInUsers: false }}
            className="!mb-0 !rounded-2xl !shadow-none"
            imageContainerClassName={`flex justify-center bg-black px-6 ${variant === 'panel' ? 'py-7 sm:py-9' : 'py-4'}`}
            imageClassName={`h-auto w-full ${variant === 'panel' ? 'max-w-[360px]' : 'max-w-[180px]'}`}
            stackedContentClassName="px-6 pb-6 md:px-8"
            customTitleClassName="mb-2 text-2xl font-bold text-gray-900"
            customSuccessMessageClassName="text-lg text-gray-600"
            content={{
              title: 'Get our Deal of the Day emails',
              imageUrl:
                'https://vinc.gumlet.io/pics/specials/bf2026/deal-of-the-day-logo.png',
              imageAlt: 'Deal of the Day',
              introHtml: `<p>Our very best deals! During our Black Friday sale, we&#39;ll send you a daily discount for products as low as ${currencySymbol}1. Unsubscribe at any time.</p>`,
              footerHtml:
                '<p>By signing up, you agree to receive daily emails during our sales. New subscribers also receive our regular newsletter and magic updates. <a href="/faqs/privacy/" class="underline">Privacy policy</a>.</p>',
              buttonText: 'Send me daily deals',
              submitButtonText: 'Send me daily deals',
              successMessage: 'You’re signed up for daily deals.',
              formFields: [
                {
                  name: 'name',
                  label: 'Your name',
                  required: true,
                  validation: { maxLength: 200 },
                },
                {
                  name: 'email',
                  label: 'Email address',
                  type: 'email',
                  required: true,
                  validation: { pattern: EMAIL_REGEX, maxLength: 254 },
                },
              ],
            }}
          />
          {justSubscribed && userId != null && (
            <Link
              href="/account/newsletters/"
              className="mt-3 inline-block underline"
            >
              Manage your email preferences
            </Link>
          )}
        </div>
      )}
    </section>
  )
}

export function DailyDealsSubscribedMessage() {
  return (
    <div role="status">
      <p>You’re already signed up for daily deals.</p>
      <Link href="/account/newsletters/" className="underline">
        Manage your email preferences
      </Link>
    </div>
  )
}
